import { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isNil } from 'lodash';
import { CSSProperties, useEffect, useState } from 'react';
import { Card, CardProps } from './Card';
import { DragProxy } from './DragProxy';
import { useDraggableContext } from './DraggableContext';
import { getDraggableId } from './DraggableContextProvider';

interface CardListItemProps {
  groupId?: UniqueIdentifier;
}

export function CardListItem({ groupId, ...cardProps }: CardListItemProps & CardProps) {
  const { isDragEnabled } = useDraggableContext();
  if (isDragEnabled && !isNil(groupId)) {
    return <DraggableCard {...cardProps} {...{ groupId }} />;
  } else {
    return <Card {...cardProps} />;
  }
}

function DraggableCard({
  groupId,
  ...cardProps
}: CardListItemProps & CardProps & { groupId: UniqueIdentifier }) {
  const { workflowTemplate, viewTemplate, entity } = cardProps;
  const entityId = entity.id;

  const { activeCards, activeGroupId, isDragSortEnabled } = useDraggableContext();
  const [shouldRender, setShouldRender] = useState(true);
  const draggableId = getDraggableId({ groupId, entityId, activeGroupId, activeCards });
  const firstActiveCard = activeCards[0];
  const isFirstActiveCard = firstActiveCard?.entity.id === entityId;
  const isActiveCard = activeCards.some(activeCard => activeCard.entity.id === entityId);

  const { setNodeRef, attributes, listeners, transform, transition } = useSortable({
    id: draggableId,
    data: {
      type: 'card',
      entityId,
      groupId,
      entity,
    },
  });

  const shouldNotRenderWhenDragSorting = isDragSortEnabled && isActiveCard && !isFirstActiveCard;
  const shouldNotRenderWhenDragging = !isDragSortEnabled && isActiveCard;

  useEffect(() => {
    // delay removing active cards briefly so dnd-kit has time to calculate the
    // correct position for the drag proxy
    if (shouldNotRenderWhenDragSorting || shouldNotRenderWhenDragging) {
      const timer = requestAnimationFrame(() => setShouldRender(false));
      return () => cancelAnimationFrame(timer);
    } else {
      setShouldRender(true);
    }
  }, [shouldNotRenderWhenDragSorting, shouldNotRenderWhenDragging]);

  // If the card is active but not the first active card, don't render it. The
  // first active card will be rendered as the placeholder for all active cards.
  // If drag sorting is not enabled, no placeholder is rendered.
  if (!shouldRender) {
    return null;
  }

  const applyTransform = !isNil(transform) && isDragSortEnabled;
  const style: CSSProperties = {
    transform: applyTransform ? CSS.Translate.toString(transform) : undefined,
    transition,
    opacity: isActiveCard ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={style} tabIndex={-1}>
      {isFirstActiveCard && (
        <DragProxy {...{ workflowTemplate, viewTemplate }} activeCards={[firstActiveCard]} />
      )}
      {!isFirstActiveCard && <Card {...cardProps} />}
    </div>
  );
}
